import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import BarGraph from "./BarGraph";
import PropertyStatusGraph from "./PropertyStatusGraph";
import LeadsStages from "../../components/dashboard/leads/LeadsStages";
import FollowUpTasks from "../../components/dashboard/tasks/FollowUpTasks";
import { Performers } from "../../components/dashboard/Performers";
import {
  DRAFT_STATUS_ID,
  PENDING_STATUS_ID,
  PUBLISHED_STATUS_ID,
  UNPUBLISHED_STATUS_ID,
} from "../../constants/property";
import { useGetAccountsDashboardQuery } from "../../redux/api/account";
import AccountsCards from "../../components/dashboard/accounts/AccountsCards";
import ChartsSection from "../../components/dashboard/accounts/ChartSection";
import AccountsHeader from "../../components/dashboard/accounts/Header";

const propertyStatuses = {
  [UNPUBLISHED_STATUS_ID]: "Un Published",
  [PUBLISHED_STATUS_ID]: "Published",
  [PENDING_STATUS_ID]: "Pending",
  [DRAFT_STATUS_ID]: "Draft",
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const monthLabelToNumber = (label) => {
  if (!label) return 1;
  const shortLabel = label.slice(0, 3); // e.g., "October" → "Oct"
  const index = MONTHS.indexOf(shortLabel);
  return index >= 0 ? index + 1 : 1;
};

const now = new Date();
const CURRENT_MONTH_LABEL = MONTHS[now.getMonth()];
const CURRENT_YEAR = now.getFullYear();

const RenderGroup = ({ id, currentAgency, data, isFetching, isSuccess }) => {
  const [month, setMonth] = useState(CURRENT_MONTH_LABEL);
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const monthNum = monthLabelToNumber(month);
  const yearNum = Number(year);
  const {
    data: accountsData,
    isLoading,
    isError,
  } = useGetAccountsDashboardQuery(
    currentAgency?.agencyid
      ? { agencyId: currentAgency.agencyid, month: monthNum, year: yearNum }
      : { agencyId: 0, month: monthNum, year: yearNum },
    { skip: !currentAgency?.agencyid }
  );

  const totalBalance = accountsData?.totalBalance ?? 0;
  const totalAdvance = accountsData?.totalAdvance ?? 0;
  const totalExpenses = accountsData?.totalexpanses ?? 0;
  const totalInstallmentPaid = accountsData?.totalinstallmentpaid ?? 0;
  const totalInstallmentNotPaid = accountsData?.totalinstallmentnotpaid ?? 0;
  const totalSalaries = accountsData?.totalsalaries ?? 0;
  const totalDealsAmount = accountsData?.totaldealsamount ?? 0;

  const incomeVsExpensesSeries = useMemo(() => {
    if (!accountsData) return [];
    return [
      {
        name: accountsData.monthwiseincome?.name || "Income",
        data: Array.isArray(accountsData.monthwiseincome?.data)
          ? accountsData.monthwiseincome.data
          : [],
      },
      {
        name: accountsData.monthlyExpensesTotals?.name || "Expenses",
        data: Array.isArray(accountsData.monthlyExpensesTotals?.data)
          ? accountsData.monthlyExpensesTotals.data
          : [],
      },
    ];
  }, [accountsData]);

  const profitTrendSeries = useMemo(() => {
    if (!accountsData) return [];

    const incomeArr = Array.isArray(accountsData.monthwiseincome?.data)
      ? accountsData.monthwiseincome.data
      : [];
    const expenseArr = Array.isArray(accountsData.monthlyExpensesTotals?.data)
      ? accountsData.monthlyExpensesTotals.data
      : [];

    const profitArr = incomeArr.map((inc) => inc);

    return [{ name: "Profit", data: profitArr }];
  }, [accountsData]);

  const incomeVsExpensesOptions = useMemo(
    () => ({
      legend: { show: true },
      xaxis: { categories: MONTHS },
      colors: ["#10b981", "#f59e0b"],
    }),
    []
  );

  const profitTrendOptions = useMemo(
    () => ({
      legend: { show: false },
      xaxis: { categories: MONTHS },
      colors: ["#10b981"],
      markers: {
        size: 4,
        colors: ["#fff"],
        strokeColors: "#10b981",
        strokeWidth: 2,
        hover: { size: 5, colors: ["#fff"], strokeColors: "#10b981" },
      },
    }),
    []
  );

  switch (id) {
    case "AccountsCount":
      return (
        <Box
          sx={{
            // display: "grid",
            // gridTemplateColumns: "repeat(auto-fit,minmax(285px,1fr))",
            p: 3,
          }}
        >
          <AccountsHeader
            month={month}
            setMonth={setMonth}
            year={year}
            setYear={setYear}
          />
          <AccountsCards
            totalBalance={totalInstallmentPaid}
            totalAdvance={totalAdvance}
            totalExpenses={totalExpenses}
            totalInstallmentPaid={totalInstallmentPaid}
            totalInstallmentNotPaid={totalInstallmentNotPaid}
            totalSalaries={totalSalaries}
            totalDealsAmount={totalDealsAmount}
            currentAgency={currentAgency}
          />
        </Box>
      );
    case "AccountsCharts":
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(285px,1fr))",
            p: 2,
          }}
        >
          <ChartsSection
            incomeVsExpensesOptions={incomeVsExpensesOptions}
            incomeVsExpensesSeries={incomeVsExpensesSeries}
            profitTrendOptions={profitTrendOptions}
            profitTrendSeries={profitTrendSeries}
            currentAgency={currentAgency}
          />
        </Box>
      );
    case "showLeads":
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(285px,1fr))",
            mb: 3,
          }}
        >
          <LeadsStages agencyId={currentAgency?.agencyid} />
        </Box>
      );

    case "showTasks":
      return (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(285px,1fr))",
            mb: 3,
          }}
        >
          <FollowUpTasks agencyId={currentAgency?.agencyid} />
        </Box>
      );

    case "showPerformance":
      return (
        <Box mb={3}>
          <Performers currentAgency={currentAgency} />
        </Box>
      );

    case "showPropertiesByCity":
      const propertyStatusData =
        data?.TotalPropertiesOfAgenciesByPropertyStatus?.[0]?.PropertyCounts ||
        [];
      return (
        <>
          {propertyStatusData.length > 0 && (
            <PropertyStatusGraph
              isFetching={isFetching}
              isSuccess={isSuccess}
              labels={propertyStatusData
                .filter((item) => propertyStatuses[item.Pstatus])
                .map((item) => ({
                  id: item.Pstatus,
                  label: propertyStatuses[item.Pstatus],
                }))}
              series={propertyStatusData
                .filter((item) => propertyStatuses[item.Pstatus])
                .map((item) => item.PropertyCount)}
            />
          )}
        </>
      );

    case "showGraphs":
      const cityData =
        data?.TotalPropertiesOfAgencyByCity?.[0]?.PropertyCountByCity || [];
      const leadsByCity = data?.AgencyLeadsByCity || [];
      const leadsByType = data?.AgencyLeadsByPropertyType || [];
      const registeredByAgent =
        data?.TotalRegisteredPropertiesByAgentOfAgency || [];

      return (
        <Box mb={3}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 2,
            }}
          >
            {registeredByAgent.length > 0 && (
              <BarGraph
                title="Properties Listing Overview"
                isSuccess={isSuccess}
                isFetching={isFetching}
                chipTitle={
                  registeredByAgent.reduce(
                    (acc, item) => item.PropertyCount + acc,
                    0
                  ) + " Total Properties Listing"
                }
                seriesConfig={{
                  name: "Properties",
                  data: registeredByAgent.map((item) => item.PropertyCount),
                }}
                labels={registeredByAgent.map((item) => item.AgentName)}
                series={registeredByAgent.map((item) => item.PropertyCount)}
              />
            )}

            {cityData.length > 0 && (
              <BarGraph
                title="Properties By City"
                isFetching={isFetching}
                isSuccess={isSuccess}
                chipTitle={
                  cityData.reduce((acc, item) => item.PropertyCount + acc, 0) +
                  " Total Properties By City"
                }
                labels={cityData.map((item) => item.CityName)}
                seriesConfig={{
                  name: "Properties",
                  data: cityData.map((item) => item.PropertyCount),
                }}
              />
            )}

            {leadsByCity.length > 0 && (
              <BarGraph
                title="Leads By City"
                chipTitle={
                  leadsByCity.reduce((acc, item) => acc + item.LeadCount, 0) +
                  " Total Leads"
                }
                isFetching={isFetching}
                isSuccess={isSuccess}
                labels={leadsByCity.map((item) => item.CityName)}
                seriesConfig={{
                  name: "Leads",
                  data: leadsByCity.map((item) => item.LeadCount),
                }}
              />
            )}

            {leadsByType.length > 0 && (
              <BarGraph
                title="Leads By Property Type"
                chipTitle={
                  leadsByType.reduce((acc, item) => acc + item.LeadCount, 0) +
                  " Total Leads"
                }
                isFetching={isFetching}
                isSuccess={isSuccess}
                labels={leadsByType.map((item) => item.PropertType)}
                seriesConfig={{
                  name: "Leads",
                  data: leadsByType.map((item) => item.LeadCount),
                }}
              />
            )}
          </Box>
        </Box>
      );

    default:
      return null;
  }
};

export default RenderGroup;
